// MobileTestimonials.tsx
"use client";

import * as React from "react";
import theme, { blueGradientSxDown } from "@/theme/theme";
import { Box, Stack, Typography, keyframes } from "@mui/material";
import type { Testimonial } from "@/data/testimonials";
import { TESTIMONIALS_MOCK } from "@/data/testimonials";
import TestimonialCard from "@/components/home/testimonialsCard";

const marqueeLeft = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;
const marqueeRight = keyframes`
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
`;

// === NEW: a consistent, compact width per card on mobile ===
// Tweak these two numbers to taste:
const ITEM_WIDTH = { xs: 340, sm: 260 }; // px
const ITEM_GAP = 1.5;

function splitIntoTwoRows<T>(items: T[]): [T[], T[]] {
    const a: T[] = [];
    const b: T[] = [];
    items.forEach((it, i) => (i % 2 === 0 ? a : b).push(it));
  return [a, b];
}

function MarqueeRow({
  items,
  direction = "left",
  duration = 28,
}: {
  items: Testimonial[];
  direction?: "left" | "right";
  duration?: number;
}) {
  const anim = direction === "left" ? marqueeLeft : marqueeRight;
  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        position: "relative",
        "&::before, &::after": {
          content: '""', position: "absolute", top: 0, bottom: 0, width: 32, zIndex: 2, pointerEvents: "none",
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& [data-track]": { animation: "none !important", transform: "none !important" },
        },
      }}
    >
      <Stack
        direction="row"
        useFlexGap
        gap={ITEM_GAP}
        data-track
        sx={{
          width: "max-content",
          animation: `${anim} ${duration}s linear infinite`,
          willChange: "transform",
          "&:hover": { animationPlayState: "paused" },
          px: 1,
        }}
      >
        {[...items, ...items].map((data, i) => (
          <Box 
            key={`${direction}-${data.id}-${i}`} 
            sx={{ flex: "0 0 auto", width: ITEM_WIDTH }}
          >
            <TestimonialCard data={data} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default function MobileTestimonials() {
  const [row1, row2] = React.useMemo<[Testimonial[], Testimonial[]]>(
    () => splitIntoTwoRows(TESTIMONIALS_MOCK),
    []
  );

  return (
    <Stack width="100%" alignItems="center" py={5} px={2} gap={3}
      sx={{
        display: { xs: "flex", md: "none" },
      }}
    >
      <Stack width="100%" alignItems="center" gap={1}>
        <Typography
          variant="h3"
          fontSize={"1.5rem !important"}
          fontWeight={600}
          color={theme.palette.brand?.mgsBlue1?.main ?? theme.palette.primary.main}
          textAlign="center"
        >
          <Box
            component="span"
            sx={{
                ...blueGradientSxDown(theme),
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
            }}
          >
            What our customers say
          </Box>
        </Typography>
        <Typography variant="body2" color={theme.palette.text.secondary} textAlign="center">
          Loved by teams of all sizes.
        </Typography>
      </Stack>

      <Stack width="100%" gap={2.5} sx={{
        position: "relative",
      }}>
        <Box sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "15vw",
          height: "100%",
          background: `linear-gradient(90deg, #0B0B0B 0%, rgba(11, 11, 11, 0) 100%)`,
          zIndex: 1,
        }} />
        <Box sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "15vw",
          height: "100%",
          background: `linear-gradient(-90deg, #0B0B0B 0%, rgba(11, 11, 11, 0) 100%)`,
          zIndex: 1,
        }} />
        <MarqueeRow items={row1} direction="left" duration={104} />
        <MarqueeRow items={row2} direction="right" duration={120} />
      </Stack>
    </Stack>
  );
}
